<template>
	<div ref="gProgress" v-loading.fullscreen.lock="!isPrintRoute && isBlockingProgressShowing"
		 class="qwoach-root"
		 :class="{'custom-branding-styling': isCustomStyleEnabled}"
		 :element-loading-text="progressText" :style="dynamicCssVars">
		<el-dialog :model-value="hasAccountError" title="Unable to load your account"
			width="min(90vw, 420px)" append-to-body
			:close-on-click-modal="false" :close-on-press-escape="false" :show-close="false">
			<p>We couldn't load your account. Please try again.</p>
			<template #footer>
				<qw-button @click="retryAccount">Try again</qw-button>
				<qw-button @click="signOutAccount">Sign out</qw-button>
			</template>
		</el-dialog>
		<span v-if="!canRenderPage && !hasAccountError">
			<div id="loading">
				<div class="spinner">
					<div class="bounce1"></div>
					<div class="bounce2"></div>
					<div class="bounce3"></div>
					<span v-if="isUpgrading" class="text text-center">
						<div>Upgrading {{appName}} to the latest version. Please wait...</div>
						<div v-if="totalMigrationSteps > 1" class="mt-2" style="width: 50%; margin: 0 auto;">
							<el-progress :percentage="upgradePercentCompleted"/>
							<!--							Completed {{this.currentMigrationStep}} out of {{this.totalMigrationSteps}}-->
						</div>
					</span>
				</div>

			</div>
		</span>

		<router-view v-if="canRenderPage"></router-view>
		<dialogs-wrapper transition-name="modal"></dialogs-wrapper>
		<vue-progress-bar></vue-progress-bar>
	</div>
</template>

<script>

import {AuthStateEnum} from "@shared/AuthStateEnum.js";
import {isPrintView, requiresAccount} from "@/router/utils/routeAccess.js";
import BaseComponent from "@/core/components/BaseComponent.vue";
import DatabaseMigrationManager from "@/utils/dataMigration/database/DatabaseMigrationManager";
import {appName, hexToHSL, hslObjToString, logError} from "@/utils/helpers";

export default {
	extends: BaseComponent,
	name: 'App',

	data() {
		return {
			isGlobalBlockingProgressShowing: false,
			progressText: "Loading...",
			isAuthReady: false,
			isStartingApp: false,
			checkedSessionVersion: null,
			isAccountRouteUpdating: false,
			startupError: null,
			isUpgradeChecking: false,
			isUpgrading: false,
			databaseMigrationManager: null,
			primaryColor: "pink",
			currentMigrationStep: 0,
			totalMigrationSteps: 0,
			appName,
		}
	},

	created() {
		console.log("App created")
		this.setupMigration()
		this.setupProgressBar()
		this.setupAuthState()
	},

	beforeUnmount(){
		this.stopAuthStartup?.()
	},

	watch: {
		accountContextKey(next, previous){
			if(next && previous && this.isAuthReady && requiresAccount(this.$route)){
				this.revalidateAccountRoute()
			}
		},
		'$store.state.store.authState'(state){
			if(state === AuthStateEnum.SIGNED_OUT && requiresAccount(this.$route)){
				this.$router.replace({path: this.$route.fullPath, force: true}).catch(logError)
			}
		},
	},

	methods: {
		async revalidateAccountRoute(){
			this.isAccountRouteUpdating = true
			try {
				await this.$authManager.waitUntilReady()
				await this.$router.replace({name: 'CoachMain', force: true})
			} catch(error){
				this.startupError = error
				await logError(error)
			} finally {
				this.isAccountRouteUpdating = false
			}
		},
		setupAuthState() {
			this.stopAuthStartup?.()
			this.stopAuthStartup = this.$authManager.startListenForLoginStateChange(async (userProfile, error) => {
				let version = this.$authManager.sessionVersion
				if(error || this.startupError || this.isStartingApp || this.checkedSessionVersion === version) return
				this.isStartingApp = true
				try {
					if(!this.isAuthReady) await this.setupEmbeddedState()
					if(version !== this.$authManager.sessionVersion) return
					if(userProfile && window.fcWidget){
						window.fcWidget.setExternalId(userProfile.id)
						window.fcWidget.user.setEmail(userProfile.email)
					}
					this.$Progress.finish()
					if(userProfile) await this.upgradeDataBaseSchema()
					if(version !== this.$authManager.sessionVersion) return
					this.checkedSessionVersion = version
					this.isAuthReady = true
				} catch(error){
					if(version === this.$authManager.sessionVersion){
						this.startupError = error
						this.isUpgradeChecking = false
						this.isUpgrading = false
					}
					await logError(error)
				} finally {
					this.isStartingApp = false
					if(version !== this.$authManager.sessionVersion) this.setupAuthState()
				}
			})
		},

		async retryAccount(){
			this.startupError = null
			try {
				this.setupAuthState()
				if(this.$store.state.store.authState === AuthStateEnum.ERROR){
					await this.$authManager.retryInitialization()
				}
				await this.$router.replace({path: this.$route.fullPath, force: true})
			} catch(error){
				await logError(error)
			}
		},

		async signOutAccount(){
			try {
				this.startupError = null
				let name = await this.$authManager.logout()
				await this.$router.replace({name})
			} catch(error){
				await logError(error)
			}
		},

		async getScopomaxAssessment(){
			let assessment = await this.dataManager.getAssessmentPublic("Corp9wBL7BSHMgQf4mMnOyS31mD3", "bZZgExRrkiiVVJR3Nzvu")
			console.log("getScopomaxAssessment: ", JSON.stringify(assessment))

		},

		async setupEmbeddedState() {
			let searchParams = new URL(location.href).searchParams
			let isQwoachEmbedded = searchParams.get('app-embedded') === "true" || false
			let embeddedUid = searchParams.get('embedded-uid')|| null// || this.$route.params.coachId || null // this.$route.params.coachId gives false positive when in path like /coach/:coachId
			console.log("setupEmbeddedState: ", isQwoachEmbedded, ", embeddedUid = ", embeddedUid, ", this.$route.params.coachId = ", this.$route.params.coachId, ", this.$route.params = ", this.$route.params, ", location.href = ", location.href)
			if (embeddedUid) {
				let coachProfile = await this.dataManager.apiGetUserProfilePublic({uid: embeddedUid})
				// if(!this.coachProfile){
				this.$store.commit('setEmbeddedCoachProfile', coachProfile)
				// }
			}
			this.$store.commit('setIsQwoachEmbedded', isQwoachEmbedded)
			this.$store.commit('setEmbeddedUid', embeddedUid)
			console.log("this.isQwoachEmbedded = ", isQwoachEmbedded, ", embeddedUid = g", embeddedUid, ", this.$route =", this.$route, ", location.href = ", location.href)
		},


		setupProgressBar() {
			this.$Progress.start()
			this.$router.beforeEach((to, from, next) => {
				console.log("beforeEach in App.vue")
				if (to.meta.progress !== undefined) {
					let meta = to.meta.progress
					this.$Progress.parseMeta(meta)
				}
				this.$Progress.start()
				next()
			})
			this.$router.afterEach((to, from) => {
				this.$Progress.finish()
			})
		},

		setupMigration() {
			this.databaseMigrationManager = new DatabaseMigrationManager(this.$dataManager, this.$authManager, this.$analyticManager)
		},

		async upgradeDataBaseSchema() {
			try {
				this.isUpgradeChecking = true
				let needsUpgrade = await this.databaseMigrationManager.needsUpgrade()
				console.log("databaseMigrationManager: needsUpgrade = ", needsUpgrade)
				this.isUpgradeChecking = false
				if (needsUpgrade) {
					this.isUpgrading = true
					await this.databaseMigrationManager.startUpgrade((currentMigrationStep, totalMigrationSteps) => {
						this.currentMigrationStep = currentMigrationStep
						this.totalMigrationSteps = totalMigrationSteps
					})
					this.isUpgrading = false
				}
			} catch (error) {
				this.toastError("Can't start application. Please try to refresh or contact support")
				this.logError(error)
				throw error
			}

		}
	},

	computed: {
		isPrintRoute(){
			return isPrintView(this.$route)
		},
		isBlockingProgressShowing(){
			return this.isGlobalBlockingProgressShowing || this.isAccountBusy || this.isAccountRouteUpdating || this.isStartingApp
		},
		accountContextKey(){
			let {currentUser, userProfile} = this.$store.state.store
			let ref = userProfile?.currentPortalRef
			return currentUser && ref ? JSON.stringify([currentUser.uid, ref.ownerId, ref.role, ref.userId]) : null
		},
		canRenderPage(){
			let protectedPage = requiresAccount(this.$route)
			let accountChecked = this.checkedSessionVersion === this.$authManager.sessionVersion
			if(this.isPrintRoute){
				return this.isAuthReady && !this.isBlockingProgressShowing && !this.isUpgradeChecking && !this.isUpgrading
			}
			return this.isAuthReady
				&& (!protectedPage || (accountChecked && !this.isStartingApp && !this.isUpgradeChecking && !this.isUpgrading))
				&& (!this.isAccountRouteUpdating || !protectedPage)
				&& !this.isWaitingForAccount && (!this.hasAccountError || !protectedPage)
		},
		isAccountBusy(){
			return this.$store.state.store.authState === AuthStateEnum.BUSY
		},
		hasAccountError(){
			return !this.isAccountBusy && (!!this.startupError || this.$store.state.store.authState === AuthStateEnum.ERROR)
		},
		isWaitingForAccount(){
			let state = this.$store.state.store.authState
			let hasAccount = !!this.$store.state.store.currentUser
			return requiresAccount(this.$route) && state !== AuthStateEnum.READY
				&& (state !== AuthStateEnum.BUSY || !hasAccount)
		},
		isCustomStyleEnabled() {
			let coachProfile = this.embeddedCoachProfile || this.ownerProfile
			let res = coachProfile && coachProfile.isCustomStylingEnabled()
			if(this.ownerProfile && this.ownerProfile.id === this.userProfile.id) {
				res = false
			}
			console.log("isCustomStyleEnabled: ", res, ", this.ownerProfile = ", this.ownerProfile, ", this.embeddedCoachProfile = ", this.embeddedCoachProfile)
			return res
		},

		upgradePercentCompleted() {
			return Math.floor(this.currentMigrationStep / this.totalMigrationSteps * 100)
		},
		dynamicCssVars() {
			let customStyleVars = {}
			let coachProfile = this.embeddedCoachProfile || this.ownerProfile
			if (this.isCustomStyleEnabled) {
				let primaryColor = coachProfile.getPrimaryColor()
				let buttonRadiusPx = coachProfile.brandingSettings.buttonRadiusPx

				let step = 50
				let maxLum = 98
				let base = hexToHSL(primaryColor, 0)
				let {h, s, l} = base
				let hslBackground = {h, s, l: Math.min(l + step, maxLum)}
				let hslBase = {h, s, l: Math.min(hslBackground.l - 1.1 * step, maxLum - step)}
				let hslBaseHover = {h, s, l: base.l + 5}
				let hslBaseColor = hslBase
				let hslBorder = {h, s, l: hslBase.l + step / 5}

				customStyleVars = {
					'--primaryColor2': hslObjToString(base),
					'--primaryColor2_hover-color': hslObjToString(hslBaseColor),
					'--primaryColor2_l40': hslObjToString(hslBorder),
					'--primaryColor2_l60': hslObjToString(hslBackground),
					'--primaryColor2_hover': hslObjToString(hslBaseHover),
					'--button-radius-1': `${buttonRadiusPx}px`,
					'--button-radius-2': `${Math.max(0, buttonRadiusPx - 1)}px`,
				}
			}


			return customStyleVars
		}
	}
}
</script>

<style src="./assets/scss/branding-styles.scss" lang="scss"/>

<style src="./styles/theme.scss" lang="scss"/>
